import {
    Box,
    Button,
    Drawer,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    Chip,
    useMediaQuery,
    Badge,
} from '@mui/material';
import {
    WavingHand,
    Storefront,
    LocalOffer,
    Dashboard,
    Description,
    Check,
    ChevronRight,
    CheckCircle,
    Rocket,
    KeyboardArrowDownRounded,
    GroupAdd,
    QrCodeScanner,
    ReceiptLong,
} from '@mui/icons-material';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { useCookies } from 'react-cookie';
import { useFetch } from '../../../hooks/useFecth';
import {
    ADVERTISER_ROLE_GUIDES,
    canManageAdvertiserUsers,
    canViewAdvertiserRescues,
} from '../utils/advertiserPermissions';
import { getStoredUser } from '../../../services/authSession';

const STORAGE_COMPLETED = 'advertiser_onboarding_completed';
const STORAGE_STEPS = 'advertiser_onboarding_steps';
const STORAGE_REOPEN = 'advertiser_onboarding_reopen';

export default function AdvertiserSetupOnboarding({ onOpenChange }) {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const isMobile = useMediaQuery('(max-width:900px)');
    const [{ advertiser, user: cookieUser }] = useCookies(['advertiser', 'user']);
    const sessionUser = cookieUser || getStoredUser();
    const canManageUsers = canManageAdvertiserUsers(sessionUser);
    const canViewRescues = canViewAdvertiserRescues(sessionUser);
    const { data: profile, isLoading: profileLoading } = useFetch('/advertiser/profile');

    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);

    const mustCompleteContract =
        profile?.approved !== true &&
        !profile?.contract?.signedAt &&
        !['SIGNED', 'CONCLUDED'].includes(profile?.contract?.status);

    const steps = useMemo(
        () => [
            ...(mustCompleteContract
                ? [
                      {
                          id: 'contract',
                          title: 'Assinar contrato',
                          description: 'Assine o contrato para liberar o uso da plataforma',
                          path: '/anunciante/contrato',
                          icon: Description,
                          color: '#667eea',
                      },
                  ]
                : []),
            {
                id: 'dados',
                title: 'Dados da loja',
                description: 'Adicione o logo e complete as informações da sua empresa',
                path: '/anunciante/dados',
                icon: Storefront,
                color: '#11998e',
            },
            {
                id: 'ofertas',
                title: 'Ofertas',
                description: 'Cadastre suas ofertas e promoções (voucher ou cupom)',
                path: '/anunciante/ofertas',
                icon: LocalOffer,
                color: '#f5576c',
            },
            ...(canManageUsers
                ? [
                      {
                          id: 'usuarios',
                          title: 'Convites e cargos',
                          description:
                              'Convide a equipe por e-mail. Se o e-mail não chegar, em Usuários → Convites pendentes use “Copiar link” (ou “Reenviar”) e envie pelo WhatsApp. Proprietário e Administrador gerenciam usuários; Gerente cuida de ofertas e resgates; Atendente só valida códigos.',
                          path: '/anunciante/usuarios',
                          icon: GroupAdd,
                          color: '#9b59b6',
                      },
                  ]
                : []),
            {
                id: 'validar',
                title: 'Validar códigos',
                description:
                    'Busque o código do cliente; se encontrar, informe o valor da compra e confira quanto ele paga e quanto de desconto vai para o descontômetro.',
                path: '/anunciante/validar',
                icon: QrCodeScanner,
                color: '#e67e22',
            },
            ...(canViewRescues
                ? [
                      {
                          id: 'resgates',
                          title: 'Resgates e desfazer validação',
                          description:
                              'Acompanhe os códigos na lista de resgates. Gerente, Administrador e Proprietário podem desfazer uma validação se houver erro.',
                          path: '/anunciante/resgates',
                          icon: ReceiptLong,
                          color: '#16a085',
                      },
                  ]
                : []),
            {
                id: 'dashboard',
                title: 'Visão geral',
                description: 'Conheça o painel do anunciante',
                path: '/anunciante',
                icon: Dashboard,
                color: '#4facfe',
            },
        ],
        [mustCompleteContract, canManageUsers, canViewRescues],
    );

    const persistCompletedSteps = updated => {
        setCompletedSteps(updated);
        localStorage.setItem(STORAGE_STEPS, JSON.stringify(updated));
    };

    const handleCompleteStep = stepId => {
        if (completedSteps.includes(stepId)) return;
        persistCompletedSteps([...completedSteps, stepId]);
    };

    useEffect(() => {
        if (!profile?.image) return;
        setCompletedSteps(prev => {
            if (prev.includes('dados')) return prev;
            const updated = [...prev, 'dados'];
            localStorage.setItem(STORAGE_STEPS, JSON.stringify(updated));
            return updated;
        });
    }, [profile?.image]);

    useEffect(() => {
        const checkAndOpen = () => {
            if (profileLoading) return;

            const completed = localStorage.getItem(STORAGE_COMPLETED) === 'true';
            const forceReopen = localStorage.getItem(STORAGE_REOPEN) === 'true';
            const hasNoLogo = !profile?.image;
            const shouldOpen = forceReopen || (!completed && hasNoLogo);

            if (forceReopen) {
                localStorage.removeItem(STORAGE_REOPEN);
            }

            setOpen(shouldOpen);

            if (shouldOpen) {
                setMinimized(false);
            }

            if (onOpenChange) {
                onOpenChange(shouldOpen);
            }

            const saved = localStorage.getItem(STORAGE_STEPS);
            if (saved) {
                try {
                    setCompletedSteps(JSON.parse(saved));
                } catch {
                    setCompletedSteps([]);
                }
            }
        };

        checkAndOpen();

        const handleStorageChange = () => checkAndOpen();
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [onOpenChange, profile?.image, profileLoading]);

    useEffect(() => {
        if (onOpenChange) {
            onOpenChange(open && !minimized);
        }
    }, [open, minimized, onOpenChange]);

    const handleFinishOnboarding = async () => {
        try {
            await confirm({
                title: 'Finalizar tutorial?',
                description: 'Tem certeza que deseja finalizar o tutorial? Você não verá este guia novamente automaticamente.',
                confirmationText: 'Sim, finalizar',
                cancellationText: 'Cancelar',
            });

            localStorage.setItem(STORAGE_COMPLETED, 'true');
            setOpen(false);
            if (onOpenChange) onOpenChange(false);
        } catch {
            // cancelado
        }
    };

    const handleNavigate = (path, stepId) => {
        handleCompleteStep(stepId);
        navigate(path);

        if (isMobile) {
            setMinimized(true);
        }
    };

    if (!open) return null;

    if (minimized) {
        const progress = steps.length ? Math.round((completedSteps.length / steps.length) * 100) : 0;
        const pendingSteps = steps.length - completedSteps.length;

        return (
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 1300,
                }}
            >
                <Badge
                    badgeContent={pendingSteps > 0 ? pendingSteps : null}
                    color="error"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => setMinimized(false)}
                        startIcon={<Rocket />}
                        sx={{
                            borderRadius: 50,
                            px: 3,
                            py: 1.5,
                            boxShadow: 6,
                            '&:hover': {
                                boxShadow: 10,
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Tutorial {progress}%
                    </Button>
                </Badge>
            </Box>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            variant="persistent"
            PaperProps={{
                sx: {
                    border: 'none',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    width: { xs: '100%', sm: 400 },
                },
            }}
        >
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        p: 3,
                        background: theme =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Tutorial de configuração
                        </Typography>
                        <IconButton size="small" onClick={() => setMinimized(true)} sx={{ color: 'white' }} title="Minimizar">
                            <KeyboardArrowDownRounded />
                        </IconButton>
                    </Stack>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Siga os passos abaixo para configurar sua loja na plataforma
                    </Typography>
                </Box>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Box sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                        <Stack spacing={2} alignItems="center" textAlign="center">
                            <WavingHand sx={{ fontSize: 48, color: '#FFD700' }} />
                            <Typography variant="h6" fontWeight="bold">
                                Bem-vindo, anunciante!
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {!profile?.image
                                    ? 'Comece adicionando o logo e completando os dados da sua loja.'
                                    : 'Complete as etapas para aproveitar todos os recursos da área do anunciante.'}
                            </Typography>
                        </Stack>
                    </Box>

                    <Divider />

                    <List sx={{ p: 2 }}>
                        {steps.map(step => {
                            const isCompleted = completedSteps.includes(step.id);
                            const StepIcon = step.icon;
                            return (
                                <ListItem key={step.id} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleNavigate(step.path, step.id)}
                                        sx={{
                                            borderRadius: 2,
                                            border: '2px solid',
                                            borderColor: isCompleted ? 'success.main' : 'divider',
                                            bgcolor: isCompleted ? 'success.50' : 'background.paper',
                                            '&:hover': {
                                                bgcolor: isCompleted ? 'success.100' : 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Box
                                                sx={{
                                                    bgcolor: step.color,
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 40,
                                                    height: 40,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {isCompleted ? <Check /> : <StepIcon sx={{ fontSize: 20 }} />}
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {step.title}
                                                    </Typography>
                                                    {isCompleted && <Chip label="Concluído" size="small" color="success" />}
                                                </Stack>
                                            }
                                            secondary={step.description}
                                            secondaryTypographyProps={{
                                                variant: 'body2',
                                                color: 'text.secondary',
                                            }}
                                        />
                                        <ChevronRight />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>

                    <Divider />

                    <Box sx={{ p: 2, pt: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Função de cada cargo
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                            Ao convidar alguém, escolha o cargo certo. A pessoa recebe um e-mail com o link; se não
                            chegar, em Usuários → Convites pendentes você pode copiar o link e enviar manualmente (ou
                            reenviar o e-mail). O acesso só libera depois do aceite.
                        </Typography>
                        <Stack spacing={1.25}>
                            {ADVERTISER_ROLE_GUIDES.map(guide => (
                                <Box
                                    key={guide.role}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={700}>
                                        {guide.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                                        {guide.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Progresso: {completedSteps.length}/{steps.length}
                            </Typography>
                            <Chip
                                label={`${steps.length ? Math.round((completedSteps.length / steps.length) * 100) : 0}%`}
                                color="primary"
                                size="small"
                            />
                        </Stack>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleFinishOnboarding}
                            disabled={completedSteps.length < steps.length}
                            startIcon={<CheckCircle />}
                        >
                            Finalizar tutorial
                        </Button>
                        <Button variant="text" fullWidth size="small" onClick={() => setMinimized(true)}>
                            Fechar temporariamente
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
}
